import { PreparedWorkItem } from '@/models/item.models';
import { UICustomFieldOption } from '@/models/organization.models';
import { organizationData } from '@/store/helpers';
import { isNil, uniq, isEqual, startCase } from 'lodash-es';
import { computed, Ref, ComputedRef, unref } from 'vue';
import TextCustomField from '@/components/custom-fields/TextCustomField.vue';
import EmailCustomField from '@/components/custom-fields/EmailCustomField.vue';
import UrlCustomField from '@/components/custom-fields/UrlCustomField.vue';
import PhoneNumberCustomField from '@/components/custom-fields/PhoneNumberCustomField.vue';
import NumberCustomField from '@/components/custom-fields/NumberCustomField.vue';
import ChoiceCustomField from '@/components/custom-fields/ChoiceCustomField.vue';
import BooleanCustomField from '@/components/custom-fields/BooleanCustomField.vue';
import DateCustomField from '@/components/custom-fields/DateCustomField.vue';
import UserCustomField from '@/components/custom-fields/UserCustomField.vue';
import AssetCustomField from '@/components/custom-fields/AssetCustomField.vue';
import { formatLabel } from '@/utils/date';
import { OrganizationMembership } from '@/models/user.models';
import parsePhoneNumber from 'libphonenumber-js';
import { useIntl } from './internationalization';
import dayjs from '@/utils/dayjs';
import CalculatedCustomField from '@/components/custom-fields/CalculatedCustomField.vue';
import { Primitives } from '@/models/common.models';
import {
  CustomField,
  CustomFieldConditions,
  CustomFieldOptionType,
  CustomFieldTextSubType,
  CustomFieldType,
  CustomFieldNumberSubType,
  CustomFieldChoiceSubType,
  CustomFieldBooleanSubType,
  CustomFieldDateSubType,
  CustomFieldEntitySubType,
  CustomFieldCalculatedSubType,
  CustomFieldOption,
  CustomFields,
  AssetCustomFieldValue,
} from '@/api';

export function useCustomFields({
  workTypeIds,
  excludeCalculated,
}: {
  workTypeIds: Ref<Array<string>> | ComputedRef<Array<string>>;
  excludeCalculated?: boolean;
}) {
  const {
    organization,
    workTypes: allWorkTypes,
    orgMembershipCache,
  } = organizationData();

  const workTypes = computed(() => {
    return allWorkTypes.value.filter((p) => workTypeIds.value.includes(p.id));
  });

  const customFieldCollections = computed(() => {
    const customFieldCollectionIds = uniq(
      workTypes.value.map((p) => p.customFieldCollectionId)
    ).filter((id) => !!id);
    if (!customFieldCollectionIds.length) return [];
    return organization.value?.customFieldCollections.filter((c) =>
      customFieldCollectionIds.includes(c.id)
    );
  });

  const customFields = computed<
    Array<CustomField & { required: boolean }> | undefined
  >(() => {
    const shouldInclude = (f: CustomField) =>
      excludeCalculated ? f.type !== CustomFieldType.CALCULATED : true;
    if (!workTypeIds.value.length)
      return organization.value?.customFields
        .filter(shouldInclude)
        .map((f) => ({
          ...f,
          required: false,
        }));
    if (!customFieldCollections.value?.length) return [];
    const ret: Array<CustomField & { required: boolean }> = [];

    for (const collection of customFieldCollections.value) {
      for (const field of collection.customFields) {
        if (!ret.map((f) => f.id).includes(field.customFieldId)) {
          const fullField = organization.value?.customFields.find(
            (f) => f.id === field.customFieldId
          );
          if (fullField && shouldInclude(fullField)) {
            ret.push({
              ...fullField,
              required: field.required || false,
            });
          }
        }
      }
    }

    return ret;
  });

  function getItemCustomFieldKeyValues(item: PreparedWorkItem) {
    if (!Object.keys(item.customFields).length) return {};

    const getCustomFieldValueLabel = (field: CustomField): Primitives => {
      const value = item.customFields[field.id];
      if (isNil(value)) return '';

      if (field.type === CustomFieldType.CHOICE) {
        const matchingOptions =
          field.options?.filter((o) => (value as string[])?.includes(o.id)) ||
          [];
        return matchingOptions.map((o) => o.label).join(', ');
      }

      if (
        field.type === CustomFieldType.ENTITY &&
        field.subType === CustomFieldEntitySubType.USER
      ) {
        const matchingUsers = orgMembershipCache.value?.filter((user) =>
          (value as string[])?.includes(user.userId)
        );
        return matchingUsers?.map((u) => u.user.name).join(', ');
      }

      if (field.type === CustomFieldType.ASSET) {
        const matchingAssets = value as AssetCustomFieldValue[];
        return matchingAssets?.map((a) => a.fileName).join(', ');
      }

      return value as Primitives; // primitive types
    };

    const ret: { [key: CustomField['name']]: string | number } = {};
    for (const fieldId of Object.keys(item.customFields)) {
      const customField = customFields.value?.find((f) => f.id === fieldId);
      if (!customField) continue;
      ret[customField.name] = getCustomFieldValueLabel(customField).toString();
    }
    return ret;
  }

  return {
    customFieldCollections,
    customFields,
    getItemCustomFieldKeyValues,
  };
}

export const availableCustomFieldTypes: UICustomFieldOption[] = [
  {
    type: CustomFieldType.CHOICE,
    subType: CustomFieldChoiceSubType.DROPDOWN,
    optionType: CustomFieldOptionType.STRING,
    label: 'Text Options List',
    description: 'Allows single or multiple text selection from a dropdown',
    availableAttributes: ['isMultiSelect', 'options'],
    icon: 'apps_list',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.CHOICE,
    subType: CustomFieldChoiceSubType.DROPDOWN,
    optionType: CustomFieldOptionType.NUMBER,
    label: 'Number Options List',
    description: 'Allows single or multiple number selection from a dropdown',
    availableAttributes: ['isMultiSelect', 'options'],
    icon: 'text_number_list',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.TEXT,
    subType: CustomFieldTextSubType.SHORT_TEXT,
    label: 'Short Text',
    description: 'A single line of text',
    availableAttributes: ['maxLength', 'minLength'],
    icon: 'text_field_16',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.TEXT,
    subType: CustomFieldTextSubType.LONG_TEXT,
    label: 'Paragraph',
    description: 'A multiline text field',
    availableAttributes: ['maxLength', 'minLength'],
    icon: 'text-paragraph',
    iconComponent: 'custom-icon',
  },
  {
    type: CustomFieldType.TEXT,
    subType: CustomFieldTextSubType.EMAIL,
    label: 'Email',
    description: 'An email address field',
    availableAttributes: ['maxLength', 'minLength'],
    icon: 'mail',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.TEXT,
    subType: CustomFieldTextSubType.PHONE_NUMBER,
    label: 'Phone Number',
    description: 'A phone number field',
    availableAttributes: ['maxLength', 'minLength'],
    icon: 'phone',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.TEXT,
    subType: CustomFieldTextSubType.URL,
    label: 'URL',
    description: 'A URL field',
    availableAttributes: ['maxLength', 'minLength'],
    icon: 'link',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.NUMBER,
    subType: CustomFieldNumberSubType.INTEGER,
    label: 'Whole Number',
    description: 'An integer number',
    availableAttributes: ['numberRange'],
    icon: 'number',
    iconComponent: 'octicon',
  },
  {
    type: CustomFieldType.NUMBER,
    subType: CustomFieldNumberSubType.DECIMAL,
    label: 'Decimal Number',
    description: 'A decimal number',
    availableAttributes: ['numberRange'],
    icon: 'decimal_arrow_right',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.NUMBER,
    subType: CustomFieldNumberSubType.PERCENTAGE,
    label: 'Percent',
    description: 'A value between 0 and 100',
    availableAttributes: [],
    icon: 'percent',
    iconComponent: 'custom-icon',
  },
  {
    type: CustomFieldType.BOOLEAN,
    subType: CustomFieldBooleanSubType.CHECKBOX,
    label: 'Single Checkbox',
    description: 'A single checkbox for a true/false value',
    availableAttributes: [],
    icon: 'checkbox_checked',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.BOOLEAN,
    subType: CustomFieldBooleanSubType.SWITCH,
    label: 'Switch',
    description: 'A switch for a true/false value',
    availableAttributes: [],
    icon: 'toggle_left',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.DATE,
    subType: CustomFieldDateSubType.DATE,
    label: 'Date Picker',
    description: 'For entering a date from a calendar',
    availableAttributes: [],
    icon: 'calendar-day',
    iconComponent: 'custom-icon',
  },
  {
    type: CustomFieldType.DATE,
    subType: CustomFieldDateSubType.DATETIME,
    label: 'Date Time Picker',
    description: 'For entering a date and time',
    availableAttributes: [],
    icon: 'calendar_clock',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.ENTITY,
    subType: CustomFieldEntitySubType.USER,
    label: 'User Picker',
    description: 'For selecting a user(s)',
    availableAttributes: ['isMultiSelect', 'filters'],
    icon: 'person',
    iconComponent: 'octicon',
  },
  {
    type: CustomFieldType.ASSET,
    label: 'Asset Upload',
    description: 'For uploading asset(s)',
    availableAttributes: ['isMultiSelect'],
    icon: 'arrow_upload',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.CALCULATED,
    subType: CustomFieldCalculatedSubType.NUMBER,
    label: 'Calculated (Number)',
    description:
      'A field that calculates a numeric value based on other fields in the item',
    availableAttributes: ['formula'],
    icon: 'calculator_multiple',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.CALCULATED,
    subType: CustomFieldCalculatedSubType.TEXT,
    label: 'Calculated (Text)',
    description:
      'A field that calculates a string value based on other fields in the item',
    availableAttributes: ['formula'],
    icon: 'calculator_multiple',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.CALCULATED,
    subType: CustomFieldCalculatedSubType.DATE,
    label: 'Calculated (Date)',
    description:
      'A field that calculates a date value based on other fields in the item',
    availableAttributes: ['formula'],
    icon: 'calculator_multiple',
    iconComponent: 'fluent-icon',
  },
  {
    type: CustomFieldType.CALCULATED,
    subType: CustomFieldCalculatedSubType.BOOLEAN,
    label: 'Calculated (Boolean)',
    description:
      'A field that calculates a boolean (true or false) value based on other fields in the item',
    availableAttributes: ['formula'],
    icon: 'calculator_multiple',
    iconComponent: 'fluent-icon',
  },
];

export function customFieldSection(customField: CustomField) {
  return availableCustomFieldTypes.find(
    (fieldType) =>
      fieldType.type === customField.type &&
      (!fieldType.subType || fieldType.subType === customField.subType) &&
      (!fieldType.optionType || fieldType.optionType === customField.optionType)
  );
}

export function useCustomField({
  customField,
  item,
}: {
  customField?: ComputedRef<CustomField | undefined>;
  item?: ComputedRef<PreparedWorkItem>;
}) {
  const { orgMembershipCache } = organizationData();

  const fieldTypeOption = computed(() => {
    return availableCustomFieldTypes.find(
      (fieldType) =>
        fieldType.type === customField?.value?.type &&
        (!fieldType.subType ||
          fieldType.subType === customField?.value?.subType) &&
        (!fieldType.optionType ||
          fieldType.optionType === customField?.value?.optionType)
    );
  });

  const isShortOrLongText = computed(() => {
    if (customField?.value?.type !== CustomFieldType.TEXT) return false;
    return (
      customField?.value?.subType === CustomFieldTextSubType.SHORT_TEXT ||
      customField?.value?.subType === CustomFieldTextSubType.LONG_TEXT
    );
  });

  const isParagraphField = computed(() => {
    if (customField?.value?.type !== CustomFieldType.TEXT) return false;
    return customField?.value?.subType === CustomFieldTextSubType.LONG_TEXT;
  });

  const isShortTextField = computed(() => {
    if (customField?.value?.type !== CustomFieldType.TEXT) return false;
    return customField?.value?.subType === CustomFieldTextSubType.SHORT_TEXT;
  });

  const isEmailField = computed(() => {
    if (customField?.value?.type !== CustomFieldType.TEXT) return false;
    return customField?.value?.subType === CustomFieldTextSubType.EMAIL;
  });

  const isURLField = computed(() => {
    if (customField?.value?.type !== CustomFieldType.TEXT) return false;
    return customField?.value?.subType === CustomFieldTextSubType.URL;
  });

  const isPhoneNumberField = computed(() => {
    if (customField?.value?.type !== CustomFieldType.TEXT) return false;
    return customField?.value?.subType === CustomFieldTextSubType.PHONE_NUMBER;
  });

  const isNumberField = computed(() => {
    return customField?.value?.type === CustomFieldType.NUMBER;
  });

  const isOptionsField = computed(() => {
    return customField?.value?.type === CustomFieldType.CHOICE;
  });

  const isBooleanField = computed(() => {
    return customField?.value?.type === CustomFieldType.BOOLEAN;
  });

  const isDateField = computed(() => {
    return customField?.value?.type === CustomFieldType.DATE;
  });

  const isUserField = computed(() => {
    return (
      customField?.value?.type === CustomFieldType.ENTITY &&
      customField?.value?.subType === CustomFieldEntitySubType.USER
    );
  });

  const isAssetField = computed(() => {
    return customField?.value?.type === CustomFieldType.ASSET;
  });

  const isCalculatedField = computed(() => {
    return customField?.value?.type === CustomFieldType.CALCULATED;
  });

  const formattedPhoneNumber = computed(() => {
    if (!isPhoneNumberField.value || typeof fieldValue.value !== 'string')
      return null;
    const phoneNumber = parsePhoneNumber(fieldValue.value);
    if (!phoneNumber) return null;
    return phoneNumber.formatNational();
  });

  const fieldValue = computed<
    CustomFields['value'] | OrganizationMembership[] | CustomFieldOption[]
  >(() => {
    const flatItem = unref(item);
    const flatField = unref(customField);
    if (!flatItem?.customFields || !flatField) return null;
    const value = flatItem.customFields[flatField.id];
    if (isNil(value)) return null;

    if (isOptionsField.value) {
      return ((value as string[])
        .map((v) => {
          const option = flatField.options?.find((o) => o.id === v);
          return option;
        })
        .filter((o) => !!o) || []) as CustomFieldOption[];
    }

    if (isUserField.value) {
      return orgMembershipCache.value?.filter((user) =>
        (value as string[])?.includes(user.userId)
      );
    }

    return value;
  });

  const valueText = computed(() => {
    if (isNil(fieldValue.value)) return '';
    if (
      isNumberField.value &&
      customField?.value?.subType === CustomFieldNumberSubType.PERCENTAGE
    ) {
      return `${fieldValue.value || 0}%`;
    } else if (isPhoneNumberField.value) {
      return formattedPhoneNumber.value || fieldValue.value || '';
    }
    if (
      isShortOrLongText.value ||
      isEmailField.value ||
      isURLField.value ||
      isNumberField.value
    ) {
      return fieldValue.value as string;
    } else if (isOptionsField.value) {
      return (
        (fieldValue.value as CustomFieldOption[])
          ?.map((o) => o.label)
          .join(', ') || ''
      );
    } else if (isBooleanField.value) {
      return fieldValue.value ? 'Yes' : 'No';
    } else if (isDateField.value) {
      return formatLabel(fieldValue.value as string);
    } else if (isUserField.value) {
      return `${(fieldValue.value as OrganizationMembership[]).length} user(s)`;
    } else if (isAssetField.value) {
      const assets = fieldValue.value as AssetCustomFieldValue[];
      if (!assets?.length) return '';
      return assets.map((a) => a.fileName).join(', ');
    } else if (isCalculatedField.value) {
      const subType = customField?.value?.subType;
      if (subType === CustomFieldCalculatedSubType.BOOLEAN) {
        return fieldValue.value ? 'Yes' : 'No';
      } else if (subType === CustomFieldCalculatedSubType.DATE) {
        return formatLabel(fieldValue.value as string);
      } else if (subType === CustomFieldCalculatedSubType.NUMBER) {
        return (fieldValue.value as string).toString();
      }
      return (fieldValue.value as string).toString();
    } else {
      return '';
    }
  });

  const component = computed(() => {
    if (isShortOrLongText.value) {
      return TextCustomField;
    } else if (isEmailField.value) {
      return EmailCustomField;
    } else if (isURLField.value) {
      return UrlCustomField;
    } else if (isPhoneNumberField.value) {
      return PhoneNumberCustomField;
    } else if (isNumberField.value) {
      return NumberCustomField;
    } else if (isOptionsField.value) {
      return ChoiceCustomField;
    } else if (isBooleanField.value) {
      return BooleanCustomField;
    } else if (isDateField.value) {
      return DateCustomField;
    } else if (isUserField.value) {
      return UserCustomField;
    } else if (isAssetField.value) {
      return AssetCustomField;
    } else if (isCalculatedField.value) {
      return CalculatedCustomField;
    }
    return null;
  });

  return {
    fieldTypeOption,
    isShortOrLongText,
    isParagraphField,
    isShortTextField,
    isEmailField,
    isURLField,
    isPhoneNumberField,
    isNumberField,
    isOptionsField,
    isBooleanField,
    isDateField,
    isUserField,
    isAssetField,
    isCalculatedField,
    component,
    valueText,
    fieldValue,
  };
}

export function findChangedCustomField(
  before: CustomFields,
  after: CustomFields
): {
  beforeValue: CustomFields[keyof CustomFields] | null | undefined;
  afterValue: CustomFields[keyof CustomFields] | null | undefined;
  fieldId: string;
} | null {
  for (const fieldId of Object.keys(before)) {
    if (!isEqual(before[fieldId], after[fieldId])) {
      return {
        beforeValue: before[fieldId],
        afterValue: after[fieldId],
        fieldId,
      };
    }
  }

  for (const fieldId of Object.keys(after)) {
    if (!isEqual(before[fieldId], after[fieldId])) {
      return {
        beforeValue: before[fieldId],
        afterValue: after[fieldId],
        fieldId,
      };
    }
  }
  return null;
}

export function customFieldHasValue(
  val: CustomFields['value'] | null | undefined
) {
  return (
    typeof val === 'string' ||
    typeof val === 'number' ||
    typeof val === 'boolean' ||
    (Array.isArray(val) && val.length > 0)
  );
}

export function useCustomFieldAppliedFilters(
  appliedFilters: ComputedRef<CustomFieldConditions | undefined>
) {
  const { organization, orgMembershipCache } = organizationData();

  const customFields = computed(() => {
    return organization.value?.customFields.filter((f) => {
      return !!appliedFilters.value?.[f.id];
    });
  });

  const textFilters = computed(() => {
    return customFields.value?.filter(
      (f) =>
        f.type === CustomFieldType.TEXT ||
        (f.type === CustomFieldType.CALCULATED &&
          f.subType === CustomFieldCalculatedSubType.TEXT)
    );
  });

  const numberFilters = computed(() => {
    return customFields.value?.filter(
      (f) =>
        f.type === CustomFieldType.NUMBER ||
        (f.type === CustomFieldType.CALCULATED &&
          f.subType === CustomFieldCalculatedSubType.NUMBER)
    );
  });

  const choiceFilters = computed(() => {
    return customFields.value?.filter((f) => f.type === CustomFieldType.CHOICE);
  });

  const booleanFilters = computed(() => {
    return customFields.value?.filter(
      (f) =>
        f.type === CustomFieldType.BOOLEAN ||
        (f.type === CustomFieldType.CALCULATED &&
          f.subType === CustomFieldCalculatedSubType.BOOLEAN)
    );
  });

  const dateFilters = computed(() => {
    return customFields.value?.filter(
      (f) =>
        f.type === CustomFieldType.DATE ||
        (f.type === CustomFieldType.CALCULATED &&
          f.subType === CustomFieldCalculatedSubType.DATE)
    );
  });

  const userFilters = computed(() => {
    return customFields.value?.filter(
      (f) =>
        f.type === CustomFieldType.ENTITY &&
        f.subType === CustomFieldEntitySubType.USER
    );
  });

  const assetFilters = computed(() => {
    return customFields.value?.filter((f) => f.type === CustomFieldType.ASSET);
  });

  const appliedFilterCount = computed(() => {
    return customFields.value?.length || 0;
  });

  const appliedFilterInfo = computed(() => {
    const ret: {
      id: string;
      label: string;
      value: string | Array<string>;
    }[] = [];
    if (!appliedFilters.value) return ret;
    if (textFilters.value?.length || numberFilters.value?.length) {
      for (const filter of [
        ...(textFilters.value || []),
        ...(numberFilters.value || []),
      ]) {
        const appliedFilter = appliedFilters.value[filter.id];
        if (appliedFilter) {
          const operator = appliedFilter.all?.[0]?.operator;
          if (operator) {
            const text = `${startCase(operator)} ${
              appliedFilter.all?.[0]?.value
            }${
              filter.subType === CustomFieldNumberSubType.PERCENTAGE ? '%' : ''
            }`;
            ret.push({
              id: filter.id,
              label: filter.name,
              value: text,
            });
          }
        }
      }
    }
    if (choiceFilters.value?.length) {
      for (const filter of choiceFilters.value) {
        const appliedFilter = appliedFilters.value[filter.id];
        if (appliedFilter) {
          const optionLabels = (appliedFilter.all?.[0]?.value as string[])
            ?.map((f) => {
              const option = filter.options?.find((o) => o.id === f);
              return option?.label;
            })
            .filter((o) => !!o);
          if (!optionLabels?.length) continue;
          ret.push({
            id: filter.id,
            label: filter.name,
            value: optionLabels as Array<string>,
          });
        }
      }
    }

    if (dateFilters.value?.length) {
      for (const filter of dateFilters.value) {
        const appliedFilter = appliedFilters.value[filter.id];
        if (appliedFilter) {
          const greaterThanVal = appliedFilter.all?.find(
            (f) => f.operator === 'greaterThanInclusive'
          )?.value;
          const lessThanVal = appliedFilter.all?.find(
            (f) => f.operator === 'lessThanInclusive'
          )?.value;
          const textValArr = [];
          const { dateTimeFormat } = useIntl();
          const formatToUse =
            filter.subType === CustomFieldDateSubType.DATETIME
              ? dateTimeFormat.value.fullDateTime
              : dateTimeFormat.value.date;
          if (greaterThanVal) {
            textValArr.push(
              `After ${dayjs(greaterThanVal as string).format(formatToUse)}`
            );
          }
          if (lessThanVal) {
            textValArr.push(
              `Before ${dayjs(lessThanVal as string).format(formatToUse)}`
            );
          }
          if (!textValArr.length) continue;
          ret.push({
            id: filter.id,
            label: filter.name,
            value: textValArr,
          });
        }
      }
    }

    if (userFilters.value?.length) {
      for (const filter of userFilters.value) {
        const appliedFilter = appliedFilters.value[filter.id];
        if (appliedFilter) {
          const hasNoUserApplied = appliedFilter.any?.find(
            (f) => f.operator === 'notSet'
          );
          const values = appliedFilter.any?.find((f) => f.operator === 'in')
            ?.value as Array<string>;
          if (!values?.length) continue;
          const userNames = orgMembershipCache.value
            .filter((m) => values.includes(m.userId))
            .map((m) => m.user.name);
          if (!userNames?.length && !hasNoUserApplied) continue;
          ret.push({
            id: filter.id,
            label: filter.name,
            value: hasNoUserApplied ? ['No User', ...userNames] : userNames,
          });
        }
      }
    }

    if (assetFilters.value?.length) {
      for (const filter of assetFilters.value) {
        const appliedFilter = appliedFilters.value[filter.id];
        if (appliedFilter) {
          // either has no asset or has asset
          const hasNoAssetApplied = appliedFilter.all?.find(
            (f) => f.operator === 'notSet'
          );
          const hasAssetApplied = appliedFilter.all?.find(
            (f) => f.operator === 'isSet'
          );
          const val = [];
          if (hasNoAssetApplied) {
            val.push('No Asset');
          }
          if (hasAssetApplied) {
            val.push('Has Asset');
          }
          if (!val.length) continue;
          ret.push({
            id: filter.id,
            label: filter.name,
            value: val,
          });
        }
      }
    }

    if (booleanFilters.value?.length) {
      for (const filter of booleanFilters.value) {
        const appliedFilter = appliedFilters.value[filter.id];
        if (appliedFilter) {
          const value = appliedFilter.all?.[0].value as boolean;
          if (isNil(value)) continue;
          ret.push({
            id: filter.id,
            label: filter.name,
            value: value ? 'Yes' : 'No',
          });
        }
      }
    }

    ret.sort((a, b) => a.label.localeCompare(b.label));

    return ret;
  });

  return {
    customFields,
    textFilters,
    numberFilters,
    choiceFilters,
    booleanFilters,
    dateFilters,
    userFilters,
    assetFilters,
    appliedFilterCount,
    appliedFilterInfo,
  };
}
